import { memo, ReactElement, ReactNode } from 'react';

import { isAfter, isSameDay, startOfDay } from 'date-fns';

import { Condition, FieldWithCondition, FormFieldCondition } from './types';

import { useFormContext } from '../form-provider';
import { FormStateValues } from '../types';

export type FormConditionalProps = {
  field: FieldWithCondition;
  children: ReactNode | ((isVisible: boolean) => ReactElement);
};

export const ConditionalDisplay = memo<FormConditionalProps>(({ children, field }) => {
  const {
    store: { useWatchValues },
  } = useFormContext();

  const values = useWatchValues(findQuestionIds(field.condition));
  const isVisible = shouldDisplay(field.condition, values);

  if (typeof children === 'function') {
    return children(isVisible);
  }

  if (!isVisible) {
    return null;
  }

  return children;
});

const matchesCondition = (condition: FormFieldCondition, value: any): boolean => {
  if (!value) {
    if (condition.comparator === 'not_is' || condition.comparator === 'none_of') return true;
    return false;
  }

  switch (condition.comparator) {
    case 'after': {
      if (!(value instanceof Date) || typeof condition.value !== 'string') return false;
      return isAfter(startOfDay(value), startOfDay(new Date(condition.value)));
    }

    case 'on': {
      if (!(value instanceof Date) || typeof condition.value !== 'string') return false;
      return isSameDay(new Date(value), new Date(condition.value));
    }

    case 'before': {
      if (!(value instanceof Date) || typeof condition.value !== 'string') return false;
      return isAfter(startOfDay(new Date(condition.value)), startOfDay(value));
    }

    case 'any_of': {
      const conditionValue = condition.value;
      if (!Array.isArray(conditionValue)) return false;

      if (Array.isArray(value)) {
        return value.some((val) => conditionValue.includes(val));
      }

      return conditionValue.includes(value);
    }

    case 'none_of': {
      const conditionValue = condition.value;
      if (!Array.isArray(conditionValue)) return false;

      if (Array.isArray(value)) {
        return !value.some((val) => conditionValue.includes(val));
      }

      return !conditionValue.includes(value);
    }

    case 'is': {
      if (Array.isArray(value)) {
        return value.includes(condition.value);
      }
      return value === condition.value;
    }

    case 'not_is': {
      if (Array.isArray(value)) {
        return !value.includes(condition.value);
      }
      return value !== condition.value;
    }

    default:
      return false;
  }
};

const findQuestionIds = (condition: Condition): Array<string> => {
  return condition.conditions.map((condition) => condition.questionId);
};

const shouldDisplay = (condition: Condition, state: FormStateValues = {}): boolean => {
  if (condition.conditions.length === 0) return true;

  switch (condition.op) {
    case 'AND':
      return condition.conditions.every((condition) =>
        matchesCondition(condition, state[condition.questionId])
      );
    case 'OR':
      return condition.conditions.some((condition) =>
        matchesCondition(condition, state[condition.questionId])
      );
    default:
      return true;
  }
};
